import { useId, useMemo, useState } from "react";
import { m } from "@/paraglide/messages";
import { CalcField } from "./CalcField";
import { CalcResult } from "./CalcResult";

export interface CalcFieldDef {
  id: string;
  label: () => string;
  placeholder?: string;
  /** Pre-filled raw value (e.g. tip split defaults to "1"). */
  defaultValue?: string;
  min?: number;
}

export type ResultKind = "percent" | "currency" | "number";

export interface ResultItem {
  label: () => string;
  value: number;
  kind: ResultKind;
}

export interface PercentCalcConfig {
  fields: CalcFieldDef[];
  /** Pure compute: returns result lines, or null when inputs are incomplete/invalid. */
  compute: (values: Record<string, number>) => ResultItem[] | null;
}

/**
 * Config-driven calculator widget. Manages controlled inputs and renders the
 * computed result lines. All math comes from the (pure, tested) `compute` fn —
 * this component holds no formulas, so every calculator reuses it.
 */
export function PercentCalc({ config }: { config: PercentCalcConfig }) {
  const uid = useId();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(config.fields.map((f) => [f.id, f.defaultValue ?? ""])),
  );

  const result = useMemo(() => {
    const nums: Record<string, number> = {};
    for (const f of config.fields) {
      const raw = values[f.id];
      nums[f.id] = raw === "" || raw == null ? Number.NaN : Number(raw);
    }
    return config.compute(nums);
  }, [values, config]);

  const cols = config.fields.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";

  return (
    <div className="pct-card rounded-2xl p-6 sm:p-8">
      <div className={`grid gap-4 ${cols}`}>
        {config.fields.map((f) => (
          <CalcField
            key={f.id}
            domId={`${uid}-${f.id}`}
            label={f.label()}
            placeholder={f.placeholder}
            value={values[f.id]}
            min={f.min}
            onChange={(v) => setValues((s) => ({ ...s, [f.id]: v }))}
          />
        ))}
      </div>

      <div className="mt-6">
        {result ? (
          <CalcResult items={result} />
        ) : (
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {m.pct_enter_values()}
          </p>
        )}
      </div>
    </div>
  );
}
